import { useCallback, useEffect, useState } from "react";

import { FormProvider, useForm } from "react-hook-form";
import { useParams, useRouteMatch } from "react-router-dom";

import { getSearchFilterOptions } from "@/api/endpoints/searches";
import { getTagFilterOptions } from "@/api/endpoints/tags";
import { postTrackingData } from "@/api/endpoints/track";
import ExclamationCircle from "@/assets/icons/exclamation.svg";
import Button from "@/components/Button";
import { FILTER } from "@/constants";
import FreeText from "@/containers/Filters/FilterItems/FreeText";
import InsightType from "@/containers/Filters/FilterItems/InsightType";
import MentionKind from "@/containers/Filters/FilterItems/MentionKind";
import useFilterParams from "@/hooks/useFilterParams";
import { ROUTES } from "@/routes_new";
import { FilterOptions } from "@/types/FilterOptions";
import { AccordionGroup } from "@components/Accordion";
import Drawer, {
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
} from "@components/Drawer";
import SpinnerAnimated from "@components/SpinnerAnimated";
import TextButton from "@components/TextButton";
import Typography from "@components/Typography";
import * as Sentry from "@sentry/browser";

import ChannelName from "./FilterItems/ChannelName";
import ChannelType from "./FilterItems/ChannelType";
import Language from "./FilterItems/Language";
import MinimumReach from "./FilterItems/MinimumReach";
import Sentiment from "./FilterItems/Sentiment";
import { cleanFilterQuery, prepareMultiSelects } from "./utils";

import styles from "./Filters.module.css";

const FiltersInner: React.FunctionComponent<{
  onClose: () => void;
  options: FilterOptions;
}> = ({ onClose, options }) => {
  const [filters, setFilters] = useFilterParams();

  const methods = useForm({
    values: prepareMultiSelects(cleanFilterQuery(filters)),
  });

  const { reset, handleSubmit } = methods;

  const onClear = () => {
    setFilters({ tag: filters?.tag }, true);
    onClose();
  };

  const onSubmit = useCallback(
    ({ ...values }) => {
      const cleanQuery = cleanFilterQuery(values);

      setFilters({
        ...cleanQuery,
        tag: filters.tag,
        [FILTER.PAGE]: undefined,
      });

      postTrackingData("Mentions filtered", cleanQuery);
      onClose();
    },
    [setFilters, filters, onClose]
  );

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{ height: "100%", display: "flex", flexDirection: "column" }}
        data-testid="filter-bar--container"
      >
        <DrawerContent>
          <FreeText />
          <AccordionGroup type="multiple">
            <InsightType options={options.filter_insight_types} />
            <ChannelType options={options.filter_sources} />
            <ChannelName options={options.filter_channels} />
            <Sentiment options={options.filter_sentiments} />
            <Language options={options.filter_languages} />
            <MentionKind />
          </AccordionGroup>
          <MinimumReach max={options.filter_max_reach_value} />
        </DrawerContent>
        <DrawerFooter>
          <Button
            variant="secondary"
            block
            data-testid="filter-bar--clear"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              reset();
              onClear();
            }}
          >
            {L.MENTIONS_VIEW_TOOLBAR_FILTER_RESET_BUTTON_LABEL}
          </Button>
          <Button
            variant="primary"
            block
            type="submit"
            data-testid="filter-bar--submit"
          >
            {L.MENTIONS_VIEW_TOOLBAR_FILTER_FILTER_BUTTON_LABEL}
          </Button>
        </DrawerFooter>
      </form>
    </FormProvider>
  );
};

const Filters = ({ open, onClose }: { onClose: () => void; open: boolean }) => {
  const [options, setOptions] = useState<FilterOptions | null>(null);
  const [error, setError] = useState<boolean>(false);

  const { uuid, slug: tag } = useParams<{ uuid: string; slug: string }>();
  const isFavoritesPage = useRouteMatch(ROUTES.FAVORITES_ORGID)?.isExact;

  const getOptions = useCallback(() => {
    if (isFavoritesPage) return null;

    let method = null;
    if (tag) method = () => getTagFilterOptions(tag);
    if (uuid) method = () => getSearchFilterOptions(uuid);

    if (method) {
      setOptions(null); // set to null on uuid change to prevent this persisting between page swaps
      method()
        .then(({ ok, data }) => {
          if (ok && data) {
            setOptions(data);
          }
        })
        .catch((err) => {
          setError(true);
          Sentry.captureException(err);
        });
    }
  }, [uuid, tag, isFavoritesPage]);

  useEffect(() => {
    getOptions();
  }, [getOptions]);

  return (
    <Drawer open={open} onClose={onClose}>
      <DrawerHeader>
        <Typography variant="h5">
          {L.MENTIONS_VIEW_TOOLBAR_FILTER_TITLE}
        </Typography>
      </DrawerHeader>
      {options ? (
        <FiltersInner options={options} onClose={onClose} />
      ) : (
        <div className={styles.info}>
          {error ? (
            <>
              <ExclamationCircle className={styles.error} height={24} />
              <Typography>
                {L.MENTIONS_VIEW_TOOLBAR_FILTER_LOADING_OPTIONS_ERROR}
              </Typography>
              <Typography
                variant="caption"
                block
                component={TextButton}
                onClick={() => {
                  setError(false);
                  getOptions();
                }}
              >
                {L.MENTIONS_VIEW_TOOLBAR_FILTER_LOADING_OPTIONS_RETRY}
              </Typography>
            </>
          ) : (
            <>
              <SpinnerAnimated />
              <Typography>
                {L.MENTIONS_VIEW_TOOLBAR_FILTER_LOADING_OPTIONS}
              </Typography>
            </>
          )}
        </div>
      )}
    </Drawer>
  );
};

export default Filters;
